import { state, query, property } from 'lit/decorators.js';
import { directive, Directive } from 'lit/directive.js';
import { LitElement } from 'lit';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

class WindowManager {
    static init() {
        if (this.initialized)
            return;
        this.lastScrollY = window.scrollY;
        this.lastWidth = window.innerWidth;
        this.lastHeight = window.innerHeight;
        window.addEventListener('scroll', () => {
            this.lastScrollY = window.scrollY;
            this.requestTick();
        });
        window.addEventListener('resize', () => {
            this.lastWidth = window.innerWidth;
            this.lastHeight = window.innerHeight;
            this.requestTick(true); // force recalculation
        });
        this.initialized = true;
    }
    static requestTick(forceRecalc = false) {
        if (!this.ticking) {
            requestAnimationFrame(() => this.tick(forceRecalc));
            this.ticking = true;
        }
    }
    static tick(forceRecalc) {
        this.ticking = false;
        const state = {
            scrollY: this.lastScrollY,
            width: this.lastWidth,
            height: this.lastHeight,
        };
        this.subscribers.forEach((cb) => cb(state, forceRecalc));
    }
    static subscribe(cb) {
        this.subscribers.add(cb);
    }
    static unsubscribe(cb) {
        this.subscribers.delete(cb);
    }
    /**
     * Blocca lo scroll del body. Usa ref counting per gestire modal nested.
     * Chiamare unlockBodyScroll() per ogni lockBodyScroll().
     */
    static lockBodyScroll() {
        this.scrollLockCount += 1;
        if (this.scrollLockCount === 1) {
            this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${this.scrollbarWidth}px`;
        }
    }
    /**
     * Sblocca lo scroll del body. Rimuove il lock solo quando tutti i consumer hanno chiamato unlock.
     */
    static unlockBodyScroll() {
        this.scrollLockCount = Math.max(0, this.scrollLockCount - 1);
        if (this.scrollLockCount === 0) {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }
    }
    /** Ritorna la larghezza della scrollbar (utile per compensazioni) */
    static getScrollbarWidth() {
        return this.scrollbarWidth || window.innerWidth - document.documentElement.clientWidth;
    }
}
WindowManager.initialized = false;
WindowManager.subscribers = new Set();
WindowManager.lastScrollY = 0;
WindowManager.lastWidth = 0;
WindowManager.lastHeight = 0;
WindowManager.ticking = false;
// Body scroll lock (ref counting per modal nested)
WindowManager.scrollLockCount = 0;
WindowManager.scrollbarWidth = 0;

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

globalThis.__itStickyState ??= {
    activeFixedTopStickies: [],
    activeFixedBottomStickies: [],
};
const { activeFixedTopStickies, activeFixedBottomStickies } = globalThis.__itStickyState;
// ─── Controller ──────────────────────────────────────────────────────────────
class StickyController {
    setVisualOrder(order) {
        this.visualOrder = order;
    }
    get isSticky() {
        return this._isSticky;
    }
    /** Whether this controller is managing a bottom-edge element. */
    get isBottom() {
        return this.hostElement.position === 'bottom';
    }
    constructor(host, hostElement) {
        this.visualOrder = 0;
        this.windowUpdateHandler = this.onWindowUpdate.bind(this);
        this.limit = 0;
        this.originalLimit = 0;
        this.height = 0;
        this.prevHeight = 0;
        this.reservedHeight = 0;
        this._isSticky = false;
        this.host = host;
        this.hostElement = hostElement;
        host.addController(this);
    }
    hostConnected() {
        WindowManager.init();
        if (this.isBottom && this.hostElement.positionType === 'fixed') {
            // Bottom-fixed elements are always active — no scroll trigger needed.
            this.applySticky(0);
            this._isSticky = true;
        }
        else {
            this.computeLimit();
            this.originalLimit = this.hostElement.offsetTop;
            WindowManager.subscribe(this.onWindowUpdate.bind(this));
        }
        // ResizeObserver tracks height changes so stacked peers can reposition.
        if (this.hostElement.firstElementChild) {
            const content = this.hostElement.firstElementChild;
            this.resizeObserver = new ResizeObserver(() => {
                if (this.isBottom) {
                    const currentHeight = this.hostElement.getBoundingClientRect().height;
                    if (currentHeight !== this.height) {
                        this.height = currentHeight;
                        if (this._isSticky)
                            this.updateAllActiveFixedBottomPositions();
                    }
                }
                else {
                    const currentHeight = this.measureHeight();
                    // const currentHeight =
                    //   this.hostElement.getBoundingClientRect().height + Number(this.hostElement.paddingTop || 0);
                    if (currentHeight !== this.height) {
                        this.height = currentHeight;
                        if (!this._isSticky) {
                            this.reservedHeight = currentHeight;
                        }
                        this.computeLimit();
                        if (this.hostElement.stackable && this._isSticky) {
                            const offset = this.computeOffset();
                            this.hostElement.style.top = `${offset + Number(this.hostElement.paddingTop || 0)}px`;
                        }
                    }
                }
            });
            this.resizeObserver.observe(content);
        }
    }
    hostDisconnected() {
        this.host.removeController(this);
        this.resizeObserver?.disconnect();
        if (this.isBottom) {
            // Clean up the global bottom list and reposition remaining peers.
            if (this._isSticky) {
                this.resetSticky();
                this._isSticky = false;
            }
        }
        else {
            WindowManager.unsubscribe(this.onWindowUpdate.bind(this));
        }
    }
    hostUpdate() {
        if (this.isBottom) {
            // Bottom elements: only update the bottom offset when stackable and active.
            if (this._isSticky && this.hostElement.stackable) {
                this.hostElement.style.bottom = `${this.computeBottomOffset()}px`;
            }
            return;
        }
        // Top elements: recompute limit and enforce correct top position.
        this.computeLimit();
        this.prevHeight = this.measureHeight();
        // this.prevHeight = this.hostElement.getBoundingClientRect().height + Number(this.hostElement.paddingTop || 0);
        if (!this._isSticky) {
            this.reservedHeight = this.prevHeight;
        }
        // If sticky, recalculate position
        if (this._isSticky) {
            const offset = this.hostElement.stackable ? this.computeOffset() : 0;
            this.hostElement.style.top = `${offset + Number(this.hostElement.paddingTop || 0)}px`;
        }
        else {
            this.hostElement.style.top = `${Number(this.hostElement.paddingTop || 0)}px`;
        }
    }
    computeLimit() {
        if (this._isSticky) {
            // Keep original limit — getBoundingClientRect is expensive when sticky.
            this.limit = this.originalLimit;
        }
        else {
            const rect = this.hostElement.getBoundingClientRect();
            const { scrollY } = window;
            this.limit = rect.top + scrollY;
            this.originalLimit = this.limit;
            this.height = rect.height;
        }
    }
    measureHeight() {
        return this.hostElement.getBoundingClientRect().height;
    }
    // eslint-disable-next-line class-methods-use-this
    isBeforeInDOM(a, b) {
        // https:// developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition
        // this is a bitmask operation
        // eslint-disable-next-line no-bitwise
        return !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
    }
    /**
     * Walks the DOM from `document.body` using a TreeWalker and collects every
     * element for which `predicate` returns true.
     *
     * Compared to `querySelectorAll` this avoids allocating a static NodeList,
     * allows arbitrary JS predicates (not just CSS selectors), and is the
     * idiomatic API for programmatic DOM traversal.
     */
    // eslint-disable-next-line class-methods-use-this
    walkElements(predicate) {
        const results = [];
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
            acceptNode: (node) => (predicate(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
        });
        for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
            results.push(node);
        }
        return results;
    }
    getPreviousFixedStickiesHeight() {
        const myContainingBlock = this.getFixedContainingBlockFor(this.hostElement);
        return this.walkElements((el) => !!el._stickyController &&
            !!el.stackable &&
            el.positionType === 'fixed')
            .filter((sticky) => sticky !== this.hostElement &&
            this.getFixedContainingBlockFor(sticky) === myContainingBlock &&
            this.isBeforeInDOM(sticky, this.hostElement))
            .reduce((acc, sticky) => {
            const rect = sticky.getBoundingClientRect();
            return acc + rect.height + Number(sticky.paddingTop || 0);
        }, 0);
    }
    /**
     * Computes the top offset for top-edge stackable stickies.
     *
     * Visual order: active fixed-top first (activation order), then
     * non-fixed in DOM order. Returns the cumulative height of all elements
     * with a lower visual order than this one.
     */
    computeOffset() {
        if (!this.hostElement.stackable)
            return 0;
        const myContainingBlock = this.getFixedContainingBlockFor(this.hostElement);
        // Only consider fixed-top stickies within the same CSS containing block.
        const scopedFixed = activeFixedTopStickies.filter((el) => this.getFixedContainingBlockFor(el) === myContainingBlock);
        const allStickies = this.walkElements((el) => !!el._stickyController && !!el.stackable);
        const visualOrderList = [
            ...scopedFixed,
            ...allStickies.filter((el) => el.positionType !== 'fixed' && !scopedFixed.includes(el)),
        ];
        visualOrderList.forEach((el, idx) => {
            if (el._stickyController)
                el._stickyController.setVisualOrder(idx);
        });
        const myOrder = this.visualOrder;
        const relevantStickies = visualOrderList.filter((el) => el !== this.hostElement && el._stickyController && el._stickyController.visualOrder < myOrder);
        return relevantStickies.reduce((acc, sticky) => {
            const rect = sticky.getBoundingClientRect();
            // paddingTop creates visual space that must be included in the offset.
            const addPad = Number(sticky.paddingTop || 0);
            return acc + rect.height + addPad;
        }, 0);
    }
    getExtraTriggerOffset() {
        if (this.hostElement.triggerSelector) {
            const el = document.querySelector(this.hostElement.triggerSelector);
            if (el instanceof HTMLElement)
                return el.offsetHeight;
        }
        return Number(this.hostElement.triggerOffset || 0);
    }
    /**
     * Returns the nearest ancestor element that creates a CSS fixed-position
     * containing block (i.e. has a non-none `transform`, `filter`, or
     * `perspective`), or `null` when the viewport is the containing block.
     *
     * Two bottom-fixed elements that share the same containing block should
     * stack against each other; elements in different containing blocks
     * (e.g. separate Storybook story previews) are fully independent.
     */
    // eslint-disable-next-line class-methods-use-this
    getFixedContainingBlockFor(el) {
        let parent = el.parentElement;
        while (parent && parent !== document.documentElement) {
            const style = getComputedStyle(parent);
            if (style.transform !== 'none' || style.filter !== 'none' || style.perspective !== 'none') {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null; // viewport is the containing block
    }
    /**
     * Computes the bottom offset for bottom-edge stackable stickies.
     *
     * Only elements that share the same CSS fixed containing block are counted —
     * this prevents elements in separate story previews (or any transformed
     * containers) from stacking against each other.
     */
    computeBottomOffset() {
        if (!this.hostElement.stackable)
            return 0;
        const myContainingBlock = this.getFixedContainingBlockFor(this.hostElement);
        const myIndex = activeFixedBottomStickies.indexOf(this.hostElement);
        if (myIndex <= 0)
            return 0;
        return activeFixedBottomStickies
            .slice(0, myIndex)
            .filter((el) => this.getFixedContainingBlockFor(el) === myContainingBlock)
            .reduce((acc, el) => acc + el.getBoundingClientRect().height, 0);
    }
    getClasses() {
        if (!this.hostElement.stickyClassName)
            return [];
        const stickyClasses = this.hostElement.stickyClassName.split(' ').filter((c) => c.trim());
        return stickyClasses.length ? [...new Set(stickyClasses)] : [this.hostElement.stickyClassName];
    }
    onWindowUpdate(state, forceRecalc) {
        // Bottom-fixed elements activate immediately on connect and never scroll-toggle.
        if (this.isBottom)
            return;
        const currentHeight = this.measureHeight();
        // const currentHeight = this.hostElement.getBoundingClientRect().height;
        if (forceRecalc && currentHeight !== this.prevHeight) {
            this.computeLimit();
            this.prevHeight = currentHeight;
            if (!this._isSticky) {
                this.reservedHeight = currentHeight;
            }
        }
        let offset = 0;
        if (this.hostElement.stackable) {
            offset = this.computeOffset();
        }
        let triggerPoint = this.limit;
        if (this.hostElement.stackable && this.hostElement.positionType !== 'fixed') {
            // Consider previous fixed stickies height and anticipate trigger
            const prevFixedHeight = this.getPreviousFixedStickiesHeight();
            // Anticipate trigger with larger buffer to avoid overlaps
            triggerPoint = this.limit - prevFixedHeight - 40;
        }
        const shouldBeSticky = state.scrollY > triggerPoint - (offset + Number(this.hostElement.paddingTop || 0)) + this.getExtraTriggerOffset();
        if (shouldBeSticky && !this._isSticky) {
            this.applySticky(offset);
            this._isSticky = true;
        }
        else if (!shouldBeSticky && this._isSticky) {
            this.resetSticky();
            this._isSticky = false;
        }
        else if (this._isSticky && this.hostElement.stackable) {
            // Still sticky and stackable: update position in case peers changed.
            const newOffset = this.computeOffset();
            this.hostElement.style.top = `${newOffset + Number(this.hostElement.paddingTop || 0)}px`;
        }
    }
    applySticky(offset = 0) {
        const classes = this.getClasses();
        if (this.isBottom) {
            this.hostElement.classList.add(...classes, 'bs-is-fixed-bottom');
            if (!activeFixedBottomStickies.includes(this.hostElement)) {
                activeFixedBottomStickies.push(this.hostElement);
            }
            // Update all bottom positions after adding class so getBoundingClientRect
            // returns the correct rendered dimensions.
            this.updateAllActiveFixedBottomPositions();
        }
        else if (this.hostElement.positionType === 'fixed') {
            this.hostElement.classList.add(...classes, 'bs-is-fixed');
            if (!activeFixedTopStickies.includes(this.hostElement)) {
                activeFixedTopStickies.push(this.hostElement);
            }
            // Update position of all active fixed after applying classes
            // so getBoundingClientRect() returns correct dimensions.
            this.updateAllActiveFixedTopPositions();
        }
        else {
            const layoutHeight = this.reservedHeight || this.measureHeight();
            if (layoutHeight > 0) {
                this.hostElement.style.minHeight = `${layoutHeight}px`;
            }
            this.hostElement.classList.add(...classes, 'bs-is-sticky');
            this.hostElement.style.top = `${offset + Number(this.hostElement.paddingTop || 0)}px`;
        }
        this.hostElement.dispatchEvent(new CustomEvent('it-sticky-on', {
            bubbles: true,
            composed: true,
            cancelable: true,
            detail: { id: this.hostElement.id },
        }));
    }
    resetSticky() {
        const classes = this.getClasses();
        if (this.isBottom) {
            this.hostElement.classList.remove(...classes, 'bs-is-fixed-bottom');
            const idx = activeFixedBottomStickies.indexOf(this.hostElement);
            if (idx !== -1)
                activeFixedBottomStickies.splice(idx, 1);
            this.updateAllActiveFixedBottomPositions();
            this.hostElement.style.bottom = '';
        }
        else if (this.hostElement.positionType === 'fixed') {
            this.hostElement.classList.remove(...classes, 'bs-is-fixed');
            const idx = activeFixedTopStickies.indexOf(this.hostElement);
            if (idx !== -1)
                activeFixedTopStickies.splice(idx, 1);
            this.updateAllActiveFixedTopPositions();
            this.hostElement.style.top = '';
        }
        else {
            this.hostElement.classList.remove(...classes, 'bs-is-sticky');
            this.hostElement.style.top = '';
        }
        this.hostElement.dispatchEvent(new CustomEvent('it-sticky-off', {
            bubbles: true,
            composed: true,
            cancelable: true,
            detail: { id: this.hostElement.id },
        }));
    }
    /**
     * Restacks all active top-fixed stickies from the top of their respective
     * containing block downward.
     *
     * Symmetric with `updateAllActiveFixedBottomPositions`: elements grouped by
     * CSS containing block so that stickies in separate containers (e.g. different
     * Storybook previews) each start at `top: 0` independently.
     */
    updateAllActiveFixedTopPositions() {
        const groups = new Map();
        activeFixedTopStickies.forEach((el) => {
            const cb = this.getFixedContainingBlockFor(el);
            if (!groups.has(cb))
                groups.set(cb, []);
            groups.get(cb).push(el);
        });
        groups.forEach((elements) => {
            let offset = 0;
            elements.forEach((elParam) => {
                const el = elParam;
                const ctrl = el._stickyController;
                if (ctrl) {
                    const paddingTop = Number(el.paddingTop || 0);
                    el.style.top = `${offset + paddingTop}px`;
                    const rect = el.getBoundingClientRect();
                    offset += rect.height + paddingTop;
                }
            });
        });
    }
    /**
     * Restacks all active bottom-fixed stickies from the bottom of their
     * respective containing block upward.
     *
     * Elements are grouped by their CSS fixed containing block so that
     * stickies in separate containers (e.g. different Storybook previews)
     * each start at `bottom: 0` and don't affect one another.
     */
    updateAllActiveFixedBottomPositions() {
        // Group by containing block (null = viewport).
        const groups = new Map();
        activeFixedBottomStickies.forEach((el) => {
            const cb = this.getFixedContainingBlockFor(el);
            if (!groups.has(cb))
                groups.set(cb, []);
            groups.get(cb).push(el);
        });
        groups.forEach((elements) => {
            let offset = 0;
            elements.forEach((elParam) => {
                const el = elParam;
                el.style.bottom = `${offset}px`;
                offset += el.getBoundingClientRect().height;
            });
        });
    }
}

class ItSticky extends BaseComponent {
    constructor() {
        super(...arguments);
        this.paddingTop = 0;
        this.positionType = 'sticky';
        this.stackable = false;
        /**
         * Extra pixels to scroll past the element's natural position before sticky activates.
         * Useful when you want sticky to kick in only after a preceding element (e.g. a header) has scrolled away.
         */
        this.triggerOffset = 0;
        /** Which viewport edge to stick to. Use `'bottom'` together with `position-type="fixed"` for a persistent bottom bar. */
        this.position = 'top';
        this._stickyController = new StickyController(this, this);
    }
    updated() {
        if (this.id === '') {
            this.id = this._id;
        }
        if (this.positionType !== 'fixed' && this.positionType !== 'sticky') {
            // eslint-disable-next-line no-console
            console.warn("The supported values for the 'position-type' property are 'fixed' or 'sticky'.");
        }
        if (this.position !== 'top' && this.position !== 'bottom') {
            // eslint-disable-next-line no-console
            console.warn("The supported values for the 'position' property are 'top' or 'bottom'.");
        }
    }
    createRenderRoot() {
        return this;
    }
}
__decorate([
    property({ reflect: true, attribute: 'padding-top' }),
    __metadata("design:type", Object)
], ItSticky.prototype, "paddingTop", void 0);
__decorate([
    property({ reflect: true, attribute: 'sticky-class-name' }),
    __metadata("design:type", Object)
], ItSticky.prototype, "stickyClassName", void 0);
__decorate([
    property({ reflect: true, attribute: 'position-type' }),
    __metadata("design:type", Object)
], ItSticky.prototype, "positionType", void 0);
__decorate([
    property({ reflect: true, type: Boolean }),
    __metadata("design:type", Object)
], ItSticky.prototype, "stackable", void 0);
__decorate([
    property({ reflect: true, type: Number, attribute: 'trigger-offset' }),
    __metadata("design:type", Object)
], ItSticky.prototype, "triggerOffset", void 0);
__decorate([
    property({ reflect: true, attribute: 'trigger-selector' }),
    __metadata("design:type", Object)
], ItSticky.prototype, "triggerSelector", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", String)
], ItSticky.prototype, "position", void 0);
if (!customElements.get('it-sticky')) {
    customElements.define('it-sticky', ItSticky);
}

export { ItSticky as I, StickyController as S };
//# sourceMappingURL=it-sticky-DNwiRVQ6.js.map
